import { createModel } from '@rematch/core';
import { uniq } from 'underscore';
import moment from 'moment';
import { separate } from '../../modules/arrayUtils';
import { getFormattedAppointment, FormattedAppointment } from '../../components/provider/appointment/NcdAcceptanceModal/formatAppointment';
import nonCriticalException from '../../modules/exceptionLogger';

import { PublicProvider, getPayLaterEligibility } from '../../api/Providers';
import type { ApptsFromNotificationFeed } from '../../components/provider/CelebrationModal/CelebrationModal.types';
import type { RootModel, RootState } from '../models';

import { NEW_CLIENT_BOOKED_VIEWED_KEY, ADJUDICATED_APPOINTMENTS } from './constants';
import { checkShouldAdjudicateNCD } from '../../modules/provider/Appointments/NCD';
import { loadNCCPendingClientsAppointments } from '../../modules/Api';
import { ProNCCPendingAppointment } from '../../api/Providers/Appointments';

type LookUp = {
  [key: number]: number;
};

type SortAppointmentsPayload = {
  appointments: ApptsFromNotificationFeed[];
  provider: PublicProvider;
};

export type Appointments = {
  appointmentsToCelebrate: ApptsFromNotificationFeed[];
  appointmentsToApprove: FormattedAppointment[];
};

export type NCDRulesCompliance = {
  inGoodStanding: boolean;
  feePaidCount: number;
  lastChecked: string;
};

export type NCDCState = {
  formattedAppointments: FormattedAppointment[];
  appointmentsFromNotifications: ApptsFromNotificationFeed[];
  ncdRulesCompliance: NCDRulesCompliance;
  initialized: boolean;
  nccPendingAppointments: ProNCCPendingAppointment[];
};

const defaultState = () => ({
  formattedAppointments: [],
  appointmentsFromNotifications: [],
  ncdRulesCompliance: null,
  initialized: false,
  nccPendingAppointments: [],
});

export const ncdc = createModel<RootModel>()({
  name: 'ncdc',

  state: defaultState(),

  reducers: {
    onAppointmentsUpdate: (
      state: NCDCState,
      { formattedAppointments, appointmentsFromNotifications }:
      { formattedAppointments: FormattedAppointment[];
        appointmentsFromNotifications: ApptsFromNotificationFeed[];
      },
    ) => ({
      ...state,
      formattedAppointments: uniq([
        ...state.formattedAppointments,
        ...formattedAppointments,
      ]),
      appointmentsFromNotifications: uniq([
        ...state.appointmentsFromNotifications,
        ...appointmentsFromNotifications,
      ]),
      initialized: true,
    }),
    onNCDRulesComplianceUpdate: (state: NCDCState, ncdRulesCompliance: NCDRulesCompliance) => ({
      ...state,
      ncdRulesCompliance,
    }),
    onNCCPendingAppointmentsUpdate: (
      state: NCDCState,
      nccPendingAppointments: ProNCCPendingAppointment[],
    ) => ({
      ...state,
      nccPendingAppointments,
    }),
  },

  effects: dispatch => ({
    recordAppointments: async (payload: LookUp | number, state: RootState) => {
      if (payload === null || payload === undefined) return;
      const appointments = typeof payload === 'number' ? { [payload]: payload } : payload;

      const appointmentsRecorded = state.userState.values[NEW_CLIENT_BOOKED_VIEWED_KEY] || {};

      const allAppointmentsToRecord: LookUp = { ...appointments, ...appointmentsRecorded };

      await dispatch.userState.addValues({
        values: {
          [NEW_CLIENT_BOOKED_VIEWED_KEY]: allAppointmentsToRecord,
        },
        preferLocalKeys: [NEW_CLIENT_BOOKED_VIEWED_KEY],
      });
    },
    recordAdjudication: async (appointmentId: number, state: RootState) => {
      const adjudicatedAppointments = state.userState.values[ADJUDICATED_APPOINTMENTS] || {};

      dispatch.userState.addValues({
        values: {
          [ADJUDICATED_APPOINTMENTS]: {
            ...adjudicatedAppointments,
            [appointmentId]: appointmentId,
          },
        },
        preferLocalKeys: [ADJUDICATED_APPOINTMENTS],
      });
    },
    sortAppointmentsWhenReady: async (
      {
        appointments,
        provider,
      }: SortAppointmentsPayload,
    ) => {
      if (!provider.ncd_enabled) return;
      await dispatch.userState.refreshUserState();

      await dispatch.ncdc.loadFormattedAppointments({ appointments, provider });
    },
    loadFormattedAppointments: async (
      {
        appointments,
        provider,
      }: SortAppointmentsPayload,
      state: RootState,
    ) => {
      const unviewedNewClientAppointments = appointments.filter(
        appt => !state.userState.values[NEW_CLIENT_BOOKED_VIEWED_KEY]?.[appt.client_id],
      );

      const now = moment();
      let potentials: ApptsFromNotificationFeed[] = [];
      const test = appointment => moment(appointment.local_start).diff(now, 'hours') >= 36;

      [potentials] = separate(unviewedNewClientAppointments, test);
      const formattedAppointments: FormattedAppointment[] = await Promise.all(
        potentials.map(async (appointment: ApptsFromNotificationFeed) => {
          try {
            return await getFormattedAppointment(appointment, provider);
          } catch (error) {
            nonCriticalException(error);
          }
          return null;
        }).filter(Boolean),
      );

      dispatch.ncdc.onAppointmentsUpdate({
        formattedAppointments,
        appointmentsFromNotifications: unviewedNewClientAppointments,
      });
    },
    checkPayLaterEligibility: async (providerId: string | number) => {
      let eligible;
      let feePaidCount;
      const lastChecked = moment().toISOString();
      try {
        const payLaterEligibility = await getPayLaterEligibility(providerId);
        eligible = payLaterEligibility.eligible;
        feePaidCount = payLaterEligibility.feePaidCount;
      } catch {
        eligible = true;
        feePaidCount = 0;
      }

      dispatch.ncdc.onNCDRulesComplianceUpdate({
        feePaidCount,
        inGoodStanding: eligible,
        lastChecked,
      });
    },
    loadNCCPendingClientsAppointments: async (providerId: string | number) => {
      try {
        const appointments = await loadNCCPendingClientsAppointments(
          providerId,
        );
        if (appointments?.length) {
          dispatch.ncdc.onNCCPendingAppointmentsUpdate(appointments);
        }
      } catch (error) {
        nonCriticalException(error);
      }
    },
  }),

  selectors: (slice, createSelector) => ({
    appointments(models) {
      return createSelector(
        slice,
        models.proNCDExperienceEligibility.isLoading,
        models.proNCDExperienceEligibility.experience,
        models.userState.valueByKey(NEW_CLIENT_BOOKED_VIEWED_KEY),
        models.userState.valueByKey(ADJUDICATED_APPOINTMENTS),
        (
          state: NCDCState,
          proNCDExperienceEligibilityIsLoading,
          experience,
          appointmentsRecorded = {},
          adjudicatedAppointments = [],
        ): Appointments => {
          if (proNCDExperienceEligibilityIsLoading || !state.initialized) {
            return {
              appointmentsToApprove: [], appointmentsToCelebrate: [],
            };
          }

          const { formattedAppointments, appointmentsFromNotifications } = state;
          const appointmentsToApprove: FormattedAppointment[] = [];
          let appointmentsToCelebrate: ApptsFromNotificationFeed[] = [];
          const qualified = experience !== 'v1';
          let foundIds = {};

          if (qualified) {
            formattedAppointments.forEach(formattedAppointment => {
              const shouldAdjudicate = checkShouldAdjudicateNCD(
                formattedAppointment?.rawAppointment,
                adjudicatedAppointments,
                experience,
              );

              if (shouldAdjudicate) {
                appointmentsToApprove.push(formattedAppointment);
                foundIds = {
                  ...foundIds,
                  [formattedAppointment.clientId]: formattedAppointment.clientId,
                };
              }
            });

            appointmentsToCelebrate = appointmentsFromNotifications
              .filter(appointment => !foundIds[appointment.client_id]
                && !appointmentsRecorded[appointment.client_id]);
          } else {
            appointmentsToCelebrate = appointmentsFromNotifications.filter(
              appointmentFromNotifications => !appointmentsRecorded[
                appointmentFromNotifications.client_id
              ],
            );
          }

          return {
            appointmentsToCelebrate,
            appointmentsToApprove,
          };
        },
      );
    },
    ncdRulesCompliance() {
      return createSelector(
        slice,
        state => state.ncdRulesCompliance,
      );
    },
    nccPendingAppointments() {
      return createSelector(
        slice,
        state => state.nccPendingAppointments,
      );
    },
  }),
});
